<script lang="ts">
import type { RegisteredComponent } from '@builder.io/sdk-svelte';
import { Content, isPreviewing } from '@builder.io/sdk-svelte';
import { CUSTOM_COMPONENTS } from '../../../CustomComponents.js';
import { BUILDER_PUBLIC_API_KEY } from '../../../config/apiKeys.js';
import { EHeroAspectRatios, ERoutes, ETitleFontSizes } from '../../../config/enums.js';
import Breadcrumb from '../../../stories/molecules/Breadcrumb/Breadcrumb.svelte';
import EventHeader from '../../../stories/molecules/EventHeader/EventHeader.svelte';
import Hero from '../../../stories/organisms/Hero/Hero.svelte';

interface BreadcrumbItem {
    text: string;
    to: string;
    active: boolean;
}

// biome-ignore lint/suspicious/noExplicitAny: Data type from +page.server.js
let { data }: { data: any } = $props();

// shortcut to the actual content
let pageContent = $derived(data.builderProps.content?.data || {});
// we want to show unpublished content when in preview mode.
let canShowContent = $derived(pageContent || isPreviewing());

let breadCrumbList: BreadcrumbItem[] = $state([]);

let currentSlug = '';

$effect(() => {
    if (pageContent.slug !== currentSlug) {
        breadCrumbList.push({
            text: pageContent?.title ?? '',
            to: `${ERoutes.Events}${pageContent.slug}`,
            active: true,
        });
        breadCrumbList.splice(0, 0, {
            text: 'Events',
            to: ERoutes.Events,
            active: false,
        });
        currentSlug = pageContent.slug;
    }
});
</script>

{#if canShowContent}
    <!-- force re-rendering of `Content` on client-side navigation -->
    {#key pageContent}
        {#if pageContent?.thumbnail}
            <Hero
                image={{
                    src: pageContent?.thumbnail,
                    alt: pageContent?.title ?? '',
                }}
                mobileImage={{
                    src: pageContent?.thumbnailMobile || pageContent?.thumbnail,
                    alt: pageContent?.title ?? '',
                }}
                content="Events"
                titleSize={ETitleFontSizes.H2}
                aspectRatio={EHeroAspectRatios.SHOWCASE}
            />
        {/if}
        <div class="scroll-content-over-header">
            <Breadcrumb items={breadCrumbList} />

            <!-- Event header with title, date and location -->
            <EventHeader
                title={pageContent?.title}
                date={pageContent?.date}
                duration={pageContent?.duration}
                location={pageContent?.location}
            />

            <!-- Render builder content with all required props -->
            <div class="container builder-content container--event-page">
                <Content
                    model="events"
                    content={data.builderProps.content}
                    apiKey={BUILDER_PUBLIC_API_KEY}
                    customComponents={CUSTOM_COMPONENTS as RegisteredComponent[]}
                />

            </div>
        </div>
    {/key}
{:else}
    Content Not Found
{/if}

<style lang="scss">
    @use '/src/scss/global-constants/mq-config';
    @use 'sass-mq/mq' as *;

    .scroll-content-over-header {
        background: var(--colour-white);
        position: relative;
        z-index: 1;
    }

    .container {
        margin: 0 auto;
        max-width: 1300px;
        padding-bottom: var(--layout-pagegrid-margin);
        padding-top: var(--layout-pagegrid-padding-top);
    }

</style>
